/**
 * Auth session persistence.
 *
 * The session (Cognito tokens from ServicesServer `POST /auth`) is kept in
 * localStorage so it survives page reloads and new tabs, mirroring how the
 * iOS app keeps its token in UserDefaults. All access goes through this file
 * so the storage choice can be swapped later (e.g. for httpOnly cookies).
 */

export interface AuthTokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number; // seconds
  token_type: string;
  email: string;
}

export interface AuthSession {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  tokenType: string;
  email: string;
  /** Epoch milliseconds when the access/id tokens expire. */
  expiresAt: number;
}

export interface UserProfile {
  sub?: string;
  email: string;
  name?: string;
  groups: string[];
}

export const AUTH_STORAGE_KEY = "ar.auth.session";
const STORAGE_KEY = AUTH_STORAGE_KEY;

export function sessionFromTokenResponse(
  response: AuthTokenResponse,
  previous?: AuthSession,
  now: number = Date.now()
): AuthSession {
  return {
    accessToken: response.access_token,
    idToken: response.id_token,
    // the refresh endpoint echoes back the refresh token it was given,
    // but fall back to the previous one in case it ever stops doing so
    refreshToken: response.refresh_token ?? previous?.refreshToken,
    tokenType: response.token_type ?? "Bearer",
    email: response.email ?? previous?.email ?? "",
    // The id token is what GraphQL (Hasura) validates, so its own `exp`
    // claim is the source of truth; `expires_in` is only a fallback.
    expiresAt:
      jwtExpiry(response.id_token) ??
      now + (response.expires_in ?? 0) * 1000,
  };
}

/** The `exp` claim of a JWT in epoch milliseconds, if present. */
export function jwtExpiry(token: string): number | undefined {
  const exp = decodeJwtPayload(token).exp;
  return typeof exp === "number" ? exp * 1000 : undefined;
}

export function isSessionExpired(
  session: AuthSession,
  now: number = Date.now(),
  // treat tokens as expired a little early so requests don't race expiry
  skewMs: number = 60 * 1000
): boolean {
  return now >= session.expiresAt - skewMs;
}

export function loadSession(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed?.accessToken || !parsed?.refreshToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: AuthSession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // storage unavailable (private mode, quota) - session stays in memory only
  }
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Decode the (unverified) payload of a JWT. Display use only. */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export function profileFromSession(session: AuthSession): UserProfile {
  const claims = decodeJwtPayload(session.idToken);
  return {
    sub: claims.sub as string | undefined,
    email: (claims.email as string) ?? session.email,
    name: claims.name as string | undefined,
    groups: (claims["cognito:groups"] as string[]) ?? [],
  };
}
